import { v4 as uuidv4 } from 'uuid';
import type { Change } from '../../../contexts/JourneyEditContext';
import type { JourneyChange } from '../../journeys/api';

/**
 * Service for building and transforming journey change objects.
 * 
 * This service provides utilities for:
 * - Creating change objects for different operations (add/remove/replace stops, update transport)
 * - Transforming internal change format to API format
 * - Managing change sequences
 * 
 * The service acts as a bridge between the UI layer and the API layer,
 * ensuring consistent change tracking and transformation across the application.
 */

/**
 * Creates a change object for adding a new stop to the journey.
 * 
 * @param afterOrder - The order position after which the new stop will be inserted
 * @param sequence - The sequence number for this change
 * @param method - The method used to add the stop ('manual' or 'ai_assisted')
 * @param city - The city name (required for manual method)
 * @param country - The country name (required for manual method)
 * @param instructions - AI instructions (used for ai_assisted method)
 * @returns A Change object representing the add stop operation
 */
export function createAddStopChange(
  afterOrder: number,
  sequence: number,
  method: 'manual' | 'ai_assisted',
  city?: string,
  country?: string,
  instructions?: string
): Change {
  const newStopOrder = afterOrder + 1;
  const newStopId = uuidv4();
  
  return {
    type: 'add_stop',
    sequence,
    position: newStopOrder,
    stopId: newStopId,
    stop: method === 'manual' && city && country
      ? {
          id: newStopId,
          location: city,
          location_country: country,
        }
      : {
          id: newStopId,
          location: '',
          location_country: '',
        },
    method,
    instructions: instructions || '',
  };
}

/**
 * Creates a change object for replacing an existing stop with a new location.
 * 
 * @param stopId - The UUID of the stop to replace
 * @param sequence - The sequence number for this change
 * @param method - The method used to replace the stop ('manual' or 'ai_assisted')
 * @param city - The new city name (required for manual method)
 * @param country - The new country name (required for manual method)
 * @param instructions - AI instructions (used for ai_assisted method)
 * @returns A Change object representing the replace stop operation
 */
export function createReplaceStopChange(
  stopId: string,
  sequence: number,
  method: 'manual' | 'ai_assisted',
  city?: string,
  country?: string,
  instructions?: string
): Change {
  return {
    type: 'replace_stop',
    sequence,
    stopId,
    newStop: method === 'manual' && city && country
      ? {
          location: city,
          location_country: country,
        }
      : {
          location: '',
          location_country: '',
        },
    method,
    instructions: instructions || '',
  };
}

/**
 * Creates a change object for removing a stop from the journey.
 * 
 * @param stopId - The UUID of the stop to remove
 * @param stopOrder - The order number of the stop being removed (used for position-based checks)
 * @param sequence - The sequence number for this change
 * @returns A Change object representing the remove stop operation
 */
export function createRemoveStopChange(
  stopId: string,
  stopOrder: number,
  sequence: number
): Change {
  return {
    type: 'remove_stop',
    sequence,
    stopId,
    stopOrder,
  };
}

/**
 * Creates a change object for updating a leg's transport mode.
 * 
 * @param legId - The UUID of the leg to update
 * @param transportMode - The new transport mode
 * @param sequence - The sequence number for this change
 * @returns A Change object representing the transport mode update
 */
export function createUpdateLegTransportChange(
  legId: string,
  transportMode: string,
  sequence: number
): Change {
  return {
    type: 'update_leg_transport',
    sequence,
    legId,
    transportMode,
  };
}

/**
 * Transforms an internal Change object to the API format.
 * 
 * This function converts the internal change representation (used by the
 * JourneyEditContext) to the format expected by the backend API.
 * 
 * @param change - The internal Change object
 * @returns A JourneyChange object in API format
 */
export function transformChangeToAPI(change: Change): JourneyChange {
  switch (change.type) {
    case 'add_stop':
      return {
        type: 'add_stop',
        sequence: change.sequence,
        position: change.position,
        stopId: change.stopId,
        stop: {
          id: change.stopId,
          city: change.stop.location,
          country: change.stop.location_country,
        },
        method: change.method,
        instructions: change.instructions,
      };
      
    case 'remove_stop':
      return {
        type: 'remove_stop',
        sequence: change.sequence,
        stopId: change.stopId,
      };
      
    case 'replace_stop':
      return {
        type: 'replace_stop',
        sequence: change.sequence,
        stopId: change.stopId,
        newStop: {
          city: change.newStop.location,
          country: change.newStop.location_country,
        },
        method: change.method,
        instructions: change.instructions,
      };
      
    case 'update_leg_transport':
      return {
        type: 'update_leg_transport',
        sequence: change.sequence,
        legId: change.legId,
        transportMode: change.transportMode,
      };
  }
}

/**
 * Transforms an array of internal Change objects to API format.
 * 
 * This function sorts changes by sequence number and transforms each
 * change to the API format. This ensures changes are applied in the
 * correct order on the backend.
 * 
 * @param changes - Array of internal Change objects
 * @returns Array of JourneyChange objects in API format, sorted by sequence
 */
export function transformChangesToAPI(changes: Change[]): JourneyChange[] {
  // Sort by sequence to ensure changes are applied in the correct order
  const orderedChanges = [...changes].sort((a, b) => a.sequence - b.sequence);
  
  // Transform each change to API format
  return orderedChanges.map(transformChangeToAPI);
}

/**
 * Checks if a change array contains any structural changes (add/remove/replace stops).
 * 
 * Structural changes modify the journey's stop list, as opposed to
 * transport mode changes which only modify legs.
 * 
 * @param changes - Array of Change objects
 * @returns True if the array contains at least one structural change
 */
export function hasStructuralChanges(changes: Change[]): boolean {
  return changes.some(change => 
    change.type === 'add_stop' || 
    change.type === 'remove_stop' || 
    change.type === 'replace_stop'
  );
}

/**
 * Checks if a specific stop is affected by any change.
 * 
 * A stop is considered affected if it's the target of:
 * - An add operation (position matches)
 * - A remove operation (stopId matches)
 * - A replace operation (stopId matches)
 * 
 * @param changes - Array of Change objects
 * @param stopId - The UUID of the stop to check
 * @param stopOrder - The order position of the stop
 * @returns True if the stop is affected by any change
 */
export function isStopAffectedByChanges(
  changes: Change[],
  stopId: string,
  stopOrder: number
): boolean {
  return changes.some(change => 
    (change.type === 'add_stop' && change.position === stopOrder) ||
    (change.type === 'remove_stop' && change.stopId === stopId) ||
    (change.type === 'replace_stop' && change.stopId === stopId)
  );
}

/**
 * Checks if a specific leg is affected by any transport mode change.
 * 
 * @param changes - Array of Change objects
 * @param legId - The UUID of the leg to check
 * @returns True if the leg has a pending transport mode change
 */
export function isLegAffectedByChanges(
  changes: Change[],
  legId: string
): boolean {
  return changes.some(change => 
    change.type === 'update_leg_transport' && change.legId === legId
  );
}

/**
 * Gets the pending transport mode for a leg if one exists.
 * 
 * @param changes - Array of Change objects
 * @param legId - The UUID of the leg to check
 * @returns The new transport mode if pending, otherwise undefined
 */
export function getPendingTransportMode(
  changes: Change[],
  legId: string
): string | undefined {
  const change = changes.find(c => 
    c.type === 'update_leg_transport' && c.legId === legId
  );
  
  return change?.type === 'update_leg_transport' 
    ? change.transportMode 
    : undefined;
}

